import {
  ApartmentOutlined,
  BarChartOutlined,
  BookOutlined,
  BulbOutlined,
  ClusterOutlined,
  HomeOutlined,
  InboxOutlined,
  LogoutOutlined,
  MenuOutlined,
  MoonOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  SendOutlined,
  SettingOutlined,
  SunOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { NAV, NAV_HELP } from "@wms/domain";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, App, Avatar, Button, Drawer, Dropdown, Grid, Layout, Menu, Select, Spin, Tooltip, Typography } from "antd";
import type { MenuProps } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { refreshOnce, tokenExpiresAt } from "../api/client";
import { useAuthStore } from "../auth/store";
import { useUnsaved } from "../state/unsaved";
import { BarcodeSearch } from "../components/BarcodeSearch";
import { HelpHint } from "../components/HelpHint";
import { NotificationsBell } from "../components/NotificationsBell";
import { useTaskSocket } from "../hooks/useTaskSocket";
import { useNewRelease } from "../hooks/useNewRelease";
import { useUiStore } from "../ui/theme";

const { Header, Sider, Content } = Layout;

const SECTION_ICONS: Record<string, React.ReactNode> = {
  // the colours of the documents in these sections (поступление зелёное, отгрузка синяя)
  inbound: <InboxOutlined style={{ color: "var(--wms-op-receive-fg)" }} />,
  outbound: <SendOutlined style={{ color: "var(--wms-op-ship-fg)" }} />,
  warehouse: <ApartmentOutlined />,
  topology: <ClusterOutlined />,
  analytics: <BarChartOutlined />,
  catalog: <BookOutlined />,
  administration: <SettingOutlined />,
};

function Logo({ collapsed }: { collapsed: boolean }) {
  return (
    <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: 10, padding: collapsed ? "16px 0" : "16px 20px", justifyContent: collapsed ? "center" : "flex-start", color: "inherit" }}>
      <span style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #2f6fed, #7c3aed)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 15, boxShadow: "0 4px 12px rgba(47,111,237,.35)", flexShrink: 0 }}>
        W
      </span>
      {!collapsed && (
        <span style={{ lineHeight: 1.1 }}>
          <span style={{ display: "block", fontWeight: 700, fontSize: 16, letterSpacing: "-0.01em" }}>WMS</span>
          <span style={{ display: "block", fontSize: 11, color: "var(--wms-muted)" }}>управление складом</span>
        </span>
      )}
    </Link>
  );
}

const CARD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * UX3010-13 (ADR 0018): the working organization, chosen before the work. Shown only when the user may work in two or
 * more of our organizations. Switching asks first when a card has unsaved input — a contract or a document of one
 * organization is never carried into another — leaves an open card for its list and reloads every list and the rights.
 */
function CompanySwitcher({ compact }: { compact: boolean }) {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { modal, message } = App.useApp();
  const companies = me?.companies ?? [];
  if (companies.length < 2) return null;

  const switchTo = async (id: string) => {
    const target = companies.find((c) => c.id === id);
    if (!target) return;
    const store = useAuthStore.getState();
    store.setCompany(target);
    // a card of the previous organization is left for its list: nothing of it moves into the new organization
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length === 2 && (parts[1] === "new" || CARD_ID.test(parts[1]))) navigate(`/${parts[0]}`);
    await store.savePreferences({ company_id: id }).catch(() => undefined);
    await queryClient.invalidateQueries();
    await store.loadMe().catch(() => undefined);
    message.success(`Рабочая организация: ${target.name}`);
  };
  const onChange = (id: string) => {
    if (id === company?.id) return;
    if (useUnsaved.getState().dirty) {
      modal.confirm({
        title: "Сменить рабочую организацию?",
        content: "Есть несохранённые изменения — они не сохранятся. Договор и документ другой организации в новую не переносятся.",
        okText: "Сменить организацию",
        cancelText: "Остаться",
        onOk: () => {
          useUnsaved.getState().setDirty(false);
          return switchTo(id);
        },
      });
      return;
    }
    void switchTo(id);
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      {compact ? null : (
        <Typography.Text type="secondary" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
          Организация
        </Typography.Text>
      )}
      <Select
        data-testid="company-switcher"
        aria-label="Рабочая организация"
        value={company?.id ?? me?.company_id ?? undefined}
        onChange={onChange}
        options={companies.map((c) => ({ value: c.id, label: c.name }))}
        showSearch
        optionFilterProp="label"
        popupMatchSelectWidth={false}
        style={{ width: compact ? 140 : 220 }}
      />
    </span>
  );
}

export function AppLayout() {
  const { me, loadMe, logout, can, option } = useAuthStore();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const navigate = useNavigate();
  const location = useLocation();
  // UX-01: on a narrow screen the menu is a drawer with full labels, never a strip of icons
  const screens = Grid.useBreakpoint();
  const narrow = screens.lg === false;
  // BUG-009: below 768 px the header keeps icons only — search opens in a dialog, the name lives in the profile menu
  const compact = screens.md === false;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const newRelease = useNewRelease();
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("wms-sider") === "collapsed";
    } catch {
      return false;
    }
  });

  // the profile (rights, feature options, preferences) is refreshed on every page load, never only from the cache
  useEffect(() => {
    loadMe().catch(() => logout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useTaskSocket(!!me && can("tasks.tasks.read"));

  // UX3010-13: whatever changed the working organization (the switcher, another tab, a refused saved choice), every
  // list on the screen is asked again in the organization the requests now go with
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.company?.id ?? null);
  const shownCompany = useRef(companyId);
  useEffect(() => {
    if (shownCompany.current !== companyId) {
      shownCompany.current = companyId;
      void queryClient.invalidateQueries();
    }
  }, [companyId, queryClient]);

  // WMS-07: refresh the access token shortly before it expires instead of waiting for a 401 mid-work
  const accessToken = useAuthStore((s) => s.accessToken);
  useEffect(() => {
    const exp = tokenExpiresAt(accessToken);
    if (!exp) return;
    const delay = Math.max(10_000, (exp - 60) * 1000 - Date.now());
    const timer = window.setTimeout(() => {
      refreshOnce().catch(() => undefined);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [accessToken]);

  // Sidebar: a short, fixed list of everyday screens (NAV). Everything else lives on the hub pages.
  const items = useMemo<MenuProps["items"]>(() => {
    const groups = NAV.map((section) => {
      const children = section.items
        .filter((item) => can(item.permission) && (!item.option || option(item.option)))
        .map((item) => {
          const path = item.kind === "page" ? item.key : `/${item.key}`;
          // UI-19: «(?)» next to the name opens a short description and never follows the link
          const help = item.help ?? NAV_HELP[item.key];
          return {
            key: path,
            label: (
              <span style={{ display: "inline-flex", alignItems: "center", width: "100%" }}>
                <Link to={path} style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</Link>
                {help && <HelpHint subject={item.label} text={help} size={12} />}
              </span>
            ),
          };
        });
      return children.length ? { key: section.code, icon: SECTION_ICONS[section.code], label: section.label, children } : null;
    }).filter(Boolean);
    // «ИИ Помощник» (ТЗ 29.10.2026): its own item right under the dashboard, for the roles with assistant.use
    const withHelp = (path: string, label: string) => (
      <span style={{ display: "inline-flex", alignItems: "center", width: "100%" }}>
        <Link to={path} style={{ flex: 1, minWidth: 0 }}>{label}</Link>
        {NAV_HELP[path] && <HelpHint subject={label} text={NAV_HELP[path]} size={12} />}
      </span>
    );
    const assistantItem = can("assistant.use") ? [{ key: "/assistant", icon: <RobotOutlined />, label: withHelp("/assistant", "ИИ Помощник") }] : [];
    return [{ key: "/dashboard", icon: <HomeOutlined />, label: withHelp("/dashboard", "Рабочий стол") }, ...assistantItem, ...(groups as NonNullable<MenuProps["items"]>)];
    // `can` is a stable store function: the list must be rebuilt when the profile (rights) arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, option, me]);

  const selected = "/" + (location.pathname.split("/")[1] ?? "");
  const currentSection = NAV.find((section) => section.items.some((item) => (item.kind === "page" ? item.key : `/${item.key}`) === selected))?.code;
  const [openKeys, setOpenKeys] = useState<string[]>(currentSection ? [currentSection] : []);
  useEffect(() => {
    if (currentSection) setOpenKeys([currentSection]);
  }, [currentSection]);
  const savedTheme = me?.preferences?.theme;
  useEffect(() => {
    if (savedTheme === "dark" || savedTheme === "light") setTheme(savedTheme);
  }, [savedTheme, setTheme]);
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    useAuthStore.getState().savePreferences({ theme: next }).catch(() => undefined);
  };
  const userMenu: MenuProps["items"] = [
    // BUG-009: on a narrow header the name is not shown next to the avatar — the menu says who is signed in
    ...(narrow && me ? [{ key: "me", label: me.user.full_name, disabled: true }, { type: "divider" as const }] : []),
    { key: "profile", icon: <UserOutlined />, label: <Link to="/profile">Профиль и пароль</Link> },
    { key: "theme", icon: <BulbOutlined />, label: theme === "dark" ? "Светлая тема" : "Тёмная тема", onClick: toggleTheme },
    { key: "help", icon: <QuestionCircleOutlined />, label: <Link to="/help">Помощь</Link> },
    // BUG-R31-001: «Настройки» only for a role that may read them — an operator does not get an empty page
    ...(can("core.settings.read") ? [{ key: "settings", icon: <SettingOutlined />, label: <Link to="/settings">Настройки</Link> }] : []),
    { type: "divider" },
    { key: "logout", icon: <LogoutOutlined />, label: "Выйти", danger: true, onClick: () => { logout(); navigate("/login"); } },
  ];

  if (!me) return <Spin fullscreen />;
  const initials = me.user.full_name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  const dark = theme === "dark";

  const menu = (inline: boolean) => (
    <Menu
      mode="inline"
      items={items}
      selectedKeys={[selected]}
      openKeys={inline ? (collapsed && !narrow ? [] : openKeys) : NAV.map((s) => s.code)}
      onOpenChange={(keys) => setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])}
      style={{ borderRight: 0, background: "transparent" }}
    />
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {narrow ? (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          width={292}
          title={null}
          closable={false}
          styles={{ body: { padding: 0, background: "var(--wms-surface)" } }}
        >
          <Logo collapsed={false} />
          {menu(false)}
        </Drawer>
      ) : (
      <Sider
        className="wms-sider"
        width={272}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => {
          setCollapsed(value);
          try {
            localStorage.setItem("wms-sider", value ? "collapsed" : "open");
          } catch {
            /* ignore */
          }
        }}
        theme={dark ? "dark" : "light"}
        style={{ borderRight: "1px solid var(--wms-border)", background: "var(--wms-surface)", position: "sticky", top: 0, height: "100vh", overflow: "auto" }}
      >
        <Logo collapsed={collapsed} />
        {menu(true)}
      </Sider>
      )}
      <Layout>
        <Header
          className="wms-header"
          style={{
            background: dark ? "rgba(27,28,32,.85)" : "rgba(255,255,255,.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: compact ? 8 : 16,
            borderBottom: "1px solid var(--wms-border)",
            lineHeight: "60px",
            paddingInline: compact ? 12 : undefined,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: compact ? 8 : 10, minWidth: 0, flex: 1 }}>
            {narrow && (
              <Button icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} aria-label="Открыть меню" style={{ flexShrink: 0 }}>
                {compact ? null : "Меню"}
              </Button>
            )}
            <BarcodeSearch compact={compact} />
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: compact ? 2 : 6, flexShrink: 0 }}>
            <CompanySwitcher compact={compact} />
            <Tooltip title={dark ? "Светлая тема" : "Тёмная тема"}>
              <Button type="text" icon={dark ? <SunOutlined /> : <MoonOutlined />} onClick={toggleTheme} aria-label="Сменить тему" />
            </Tooltip>
            <NotificationsBell />
            <Dropdown menu={{ items: userMenu }} trigger={["click"]}>
              <span role="button" tabIndex={0} aria-label={`Профиль: ${me.user.full_name}`} style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "4px 6px 4px 4px", borderRadius: 10, marginLeft: 4 }}>
                <Avatar size={32} style={{ background: "linear-gradient(135deg, #2f6fed, #7c3aed)", fontWeight: 600 }}>
                  {initials || <UserOutlined />}
                </Avatar>
                {narrow ? null : <Typography.Text style={{ fontSize: 13 }}>{me.user.full_name}</Typography.Text>}
              </span>
            </Dropdown>
          </span>
        </Header>
        <Content style={{ padding: "22px 24px 40px" }}>
          <div className="wms-content">
            {newRelease && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16, borderRadius: 10 }}
                message="Кабинет обновился"
                description="Эта вкладка работает со старой версией: часть кнопок может открыть не то окно. Сохраните изменения и обновите страницу."
                action={
                  <Button type="primary" onClick={() => window.location.reload()}>
                    Обновить
                  </Button>
                }
              />
            )}
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
